import { useElementVisibility, watchOnce } from '@vueuse/core'
import {
  logAdClickToAY,
  logAdImpressionToAY,
} from '../third-party/useAssertiveYield'
import { useAnalytics } from '../analytics/useAnalytics'
import type { NtpAdEvent } from '../analytics/types'
import debug from '../../utils/console'
import { BREAKPOINTS } from '../../theme/common'
import { handleStickyAd } from '../../utils/ad-ops/adFunctions'
import type { AyLogImpressionEvent } from '../../types/assertive'

const logger = debug.withTag('useAdFunctions')
const useAdFunctions = () => {
  const { track } = useAnalytics()
  const { $ayManagerEnv } = useNuxtApp()
  // The ad stack is browser only. The shared Taboola and Outbrain composables
  // inject their scripts when they are first created, so a server render gets
  // no-op renderers instead of creating them.
  const noRender = async (): Promise<void> => {}
  const { callRenderResearchWidgets } = import.meta.client
    ? useOutbrainAds()
    : { callRenderResearchWidgets: noRender }
  const { callRenderTaboolaWidgets } = import.meta.client
    ? useTaboolaAds()
    : { callRenderTaboolaWidgets: noRender }

  const adClicked = (eventData: NtpAdEvent, slotId: string) => {
    logAdClickToAY(slotId)
    track('NtpAd_Clicked', eventData)
  }

  const adRendered = (
    eventData: NtpAdEvent,
    ayData: Partial<AyLogImpressionEvent>
  ) => {
    logAdImpressionToAY(ayData)
    track('NtpAd_Rendered', eventData)
  }

  /**
   * Renders content when an element becomes visible in the viewport
   * @param element - The HTML element to monitor for visibility
   * @param lazyRender - If true, the element will only be rendered when it becomes visible
   * visibilityTimeoutInMs - Delay in milliseconds before executing callback after visibility
   * AND visibilityThresholdInPercentage - Percentage of element that must be visible (default: 10%)
   * @param callbackFn - Function to execute when element becomes visible (can be sync or async)
   */
  const renderWhenCurrentElementIsVisible = (
    element: HTMLElement | null,
    lazyRender: boolean,
    sticky: boolean,
    callbackFn: () => void | Promise<void>
  ) => {
    let visibilityTimer: ReturnType<typeof setTimeout> | number = 0

    try {
      if (!element) {
        throw new Error('No element to render when visible')
      }

      if (!lazyRender) {
        if (sticky) handleStickyAd(element)

        // Handle both sync and async callbacks
        const result = callbackFn()
        if (result instanceof Promise) {
          result.catch((err) => {
            logger.error('Error in async callback for non-lazy render', err)
          })
        }
        return
      }

      const isVisible = useElementVisibility(element, {
        threshold: 1 / 100, //visibilityThresholdInPercentage
      })

      watchOnce(isVisible, () => {
        if (visibilityTimer) {
          clearTimeout(visibilityTimer)
        }

        if (isVisible.value) {
          visibilityTimer = setTimeout(() => {
            if (sticky) handleStickyAd(element)

            // Handle both sync and async callbacks
            const result = callbackFn()
            if (result instanceof Promise) {
              result.catch((err) => {
                logger.error(
                  'Error in async callback for element visibility',
                  err
                )
              })
            }
          }, 1) // visibilityTimeoutInMs
        }
      })
    } catch (err) {
      if (visibilityTimer) {
        clearTimeout(visibilityTimer)
      }
      logger.error('Unable to render element when visible', err)
    }
  }

  // Re-render the ad units for the current layout/category via the manual-event
  // dispatch WITHOUT calling changePage(), so it never counts an AY pageview.
  // Used for layout/breakpoint changes and category-tab clicks; accepted
  // NtpPage_Viewed events own pageview changes.
  const refreshAdUnits = () => {
    try {
      $ayManagerEnv?.cmd?.push(function () {
        const env = $ayManagerEnv
        if (!env) return
        env.dispatchManualEvent()
      })
      callRenderResearchWidgets()
      callRenderTaboolaWidgets()
    } catch (err) {
      logger.error('Error refreshing ad units', err)
    }
  }

  const { width } = useWindowSize()

  const currentBreakpoint = ref('MD')
  const isMobile = computed(() => {
    return Boolean(width.value <= BREAKPOINTS.LG)
  })

  const isXLDesktop = computed(() => {
    return Boolean(width.value >= BREAKPOINTS.XL)
  })

  watch(width, (newWidth) => {
    let newBreakpoint = currentBreakpoint.value
    if (newWidth >= BREAKPOINTS.LG) {
      newBreakpoint = 'LG'
    } else if (newWidth >= BREAKPOINTS.MD) {
      newBreakpoint = 'MD'
    } else if (newWidth >= BREAKPOINTS.SM) {
      newBreakpoint = 'SM'
    } else {
      newBreakpoint = 'XS'
    }
    if (newBreakpoint !== currentBreakpoint.value) {
      currentBreakpoint.value = newBreakpoint
      // A breakpoint/resize is not a navigation. Re-render the units
      // for the new layout WITHOUT counting a pageview.
      refreshAdUnits()
    }
  })

  return {
    adClicked,
    adRendered,
    renderWhenCurrentElementIsVisible,
    refreshAdUnits,
    isMobile,
    isXLDesktop,
  }
}

export default useAdFunctions
